import { AssessmentStatuses } from '@schooly/constants';

import { GetAssessmentsQuerySort } from '../assessments';
import {
  AssessmentAverageResultGroupTypeEnum as AssessmentAverageGroupTypeEnum,
  EntryCreator,
} from '../generated/schooly-api-v2/data-contracts';
import { MarkbookRecord, SubjectAverageScore } from '../generated/schooly-api-v2/data-contracts';
import { BaseComment, CommentCreator } from './base';
import { SelectListDetailResponseBody } from './endpoints/assessments';
import { GroupBase, GroupType } from './groups';
import { StringOrNull } from './misc';
import { RecurringState } from './recurring';
import { ReportBase, ReportStatuses } from './reports';
import { UserFilter } from './users';

export type { MarkbookRecord, SubjectAverageScore };

export enum AssessmentMethodType {
  Score,
  Grade,
  Comment,
}

export enum AssessmentAction {
  Publish = 'publish',
  Unpublish = 'unpublish',
}

export interface AssessmentGroup extends Omit<GroupBase, 'student_count'> {
  student_count?: number;
}

export interface AssessmentMethodBase {
  id?: string;
  score_out_of?: number | null;
  select_list_id?: string | null;
  method_type: AssessmentMethodType | null;
}

export interface AssessmentMethodScore extends AssessmentMethodBase {
  method_type: AssessmentMethodType.Score;
  score_out_of: number | null;
}

export interface AssessmentMethodGrade extends AssessmentMethodBase {
  method_type: AssessmentMethodType.Grade;
  select_list_id: string;
  select_list_name: string;
}

export interface AssessmentMethodComment extends AssessmentMethodBase {
  method_type: AssessmentMethodType.Comment;
}

export interface AssessmentMethodBlank extends AssessmentMethodBase {
  method_type: null;
}

export type AssessmentMethod =
  | AssessmentMethodScore
  | AssessmentMethodGrade
  | AssessmentMethodComment;

export interface AssessmentReport extends ReportBase {
  report_status: ReportStatuses;
}
export interface AssessmentBase {
  id: string;
  name: string;
  display_name?: string;
  status: AssessmentStatuses;
  assessment_date: string;
  publish_date: StringOrNull;
  autogenerated: boolean;
  generated_from_report_id: StringOrNull;
  methods: AssessmentMethod[];
  recurring_state: RecurringState | null;
  area_of_learning: AreaOfLearning | null;
  evaluation_type: EvaluationType;
  creator: EntryCreator;
}

export interface Assessment extends AssessmentBase {
  groups: AssessmentGroup[];
  can_be_edited?: boolean;
  reports: AssessmentReport[];
}

type AssessmentEntryBase = {
  method: { id: string; type: AssessmentMethodType };
};

export interface AssessmentEntryScore extends AssessmentEntryBase {
  score: number;
}

export interface AssessmentEntryGrade extends AssessmentEntryBase {
  select_list_option_id: string;
}

export interface AssessmentEntryComment extends AssessmentEntryBase {
  comment: string;
  creator: CommentCreator;
}

export type AssessmentEntry = AssessmentEntryScore | AssessmentEntryGrade | AssessmentEntryComment;

export interface AssessmentEntryBaseSave {
  group_id: string;
}

export interface AssessmentEntryScoreSave extends AssessmentEntryBaseSave {
  score: number | null;
}

export interface AssessmentEntryGradeSave extends AssessmentEntryBaseSave {
  select_list_option_id?: StringOrNull;
}

export interface AssessmentEntryCommentSave extends AssessmentEntryBaseSave {
  comment: StringOrNull;
  creator_relation_id: string;
}

export type AssessmentEntrySave =
  | AssessmentEntryScoreSave
  | AssessmentEntryGradeSave
  | AssessmentEntryCommentSave;

export interface UpsertDeleteAssessmentEntryBase {
  method_id?: string;
  method_type: AssessmentMethodType;
}

export interface UpsertDeleteAssessmentEntryScore extends UpsertDeleteAssessmentEntryBase {
  score: number;
}

export interface UpsertDeleteAssessmentEntryGrade extends UpsertDeleteAssessmentEntryBase {
  select_list_option_id: string;
}

export interface UpsertDeleteAssessmentEntryComment
  extends UpsertDeleteAssessmentEntryBase,
    BaseComment {}

export type UpsertDeleteAssessmentEntry =
  | UpsertDeleteAssessmentEntryScore
  | UpsertDeleteAssessmentEntryGrade
  | UpsertDeleteAssessmentEntryComment;

export interface AssessmentForReport {
  id: string;
  name: string;
  assessment_status: AssessmentStatuses;
  autogenerated: boolean;
  generated_from_report_id: StringOrNull;
  area_of_learning: AreaOfLearning | null;
  evaluation_type: EvaluationType;
  methods: AssessmentMethod[];
  assessment_date: string;
}
export type AssessmentsGrade = SelectListDetailResponseBody;
export type AssessmentsGradeOption = SelectListDetailResponseBody['options'][number];

export interface ListAssessmentsForSchool {
  filters?: Partial<UserFilter>;
  page_number?: number;
  page_size?: number;
  school_id: string;
  sort?: GetAssessmentsQuerySort[];
  query?: string;
}

export interface AreaOfLearning {
  id: string;
  name: string;
}

export enum EvaluationType {
  TutorFeedback,
  AreaOfLearning,
  Regular,
}

interface AssessmentGroupSubject {
  id: string;
  name: string;
  archived: boolean;
}

export interface AssessmentRecipientRelation {
  relation_id: string;
  entries: AssessmentEntry[];
}

export interface AssessmentGroupWithStudents extends Pick<GroupBase, 'id' | 'name'> {
  type: GroupType;
  subject: AssessmentGroupSubject | null;
  students: AssessmentRecipientRelation[];
}

export interface AssessmentWithEntries extends AssessmentBase {
  reports: AssessmentReport[];
  enterable_and_my_group: boolean;
  groups: AssessmentGroupWithStudents[];
}

export enum ExportMarkbookArrangeAssessmentsBy {
  Subject = 'subject',
  Assessment = 'assessment',
}

export enum ExportMarkbookInclude {
  Assessments = 'assessments',
  Attendance = 'attendance',
  Conduct = 'conduct',
}

export enum ExportMarkbookArrangeStudentsBy {
  AgeGroup = 'age_group',
  Group = 'group',
}

export enum MarkbookArrangeBy {
  AgeGroup = 'age_group',
  Group = 'group',
}

export { AssessmentAverageGroupTypeEnum };

export type {
  AssessmentAverageResult,
  AssessmentAverageSubjectResult,
  AssessmentEntryForMarkbook as MarkbookAssessmentEntry,
  Category as MarkbookCategory,
  SchoolRelationInfo as MarkbookStudent,
  StudentsData as MarkbookStudents,
} from '../generated/schooly-api-v2';
